<template>
  <div class="page-wrapper">
    <div class="page-container">
      <!-- Breadcrumb and Header -->
      <div class="page-header">
        <div class="flex justify-between items-center flex-wrap gap-4">
          <div>
            <div class="breadcrumb">
              <span class="crumb-link" @click="$router.push('/')">Asosiy</span>
              <span class="separator">/</span>
              <span class="crumb-current">Baza nusxasi (Backup & Rollback)</span>
            </div>
            <p class="header-desc">
              Bazaning to'liq nusxasini olish, avtomatik zaxiralash va istalgan daqiqa yoki kechagi holatiga qaytarish (Rollback)
            </p>
          </div>

          <div class="flex items-center gap-3">
            <button 
              class="btn-refresh" 
              :class="{ 'spinning': loading }" 
              @click="loadData" 
              title="Yangilash"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67"/>
              </svg>
              Yangilash
            </button>

            <!-- Xodimlarni yangilash (HEMIS Sync) tugmasi -->
            <button 
              class="btn-sync-employees"
              :disabled="syncingEmployees"
              @click="syncEmployeesDatabase"
              title="HEMIS dan faol xodimlarni yangilash (bo'shaganlarni chiqarib tashlash)"
            >
              <svg v-if="syncingEmployees" class="spinner" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span v-else>👥</span>
              {{ syncingEmployees ? 'Xodimlar yangilanmoqda...' : 'Xodimlarni yangilash (HEMIS)' }}
            </button>

            <!-- Tezkor Snapshot olish tugmasi -->
            <button 
              class="btn-create-backup"
              :disabled="creating"
              @click="openSnapshotModal"
            >
              <svg v-if="creating" class="spinner" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span v-else>⚡</span>
              {{ creating ? 'Nusxa olinmoqda...' : 'Tezkor nuqta (Snapshot) olish' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Alert Notification -->
      <div v-if="notification.text" :class="['notification-banner', notification.type]">
        <div class="flex items-center gap-2">
          <span v-if="notification.type === 'success'">✅</span>
          <span v-else-if="notification.type === 'error'">❌</span>
          <span v-else>ℹ️</span>
          <span>{{ notification.text }}</span>
        </div>
        <button class="close-notif" @click="notification.text = ''">✕</button>
      </div>

      <!-- Stat Cards -->
      <div class="summary-cards mt-4">
        <!-- Jami backup lar -->
        <div class="summary-card card-blue">
          <div class="card-content">
            <h2 class="card-value">{{ backups.length }} ta</h2>
            <p class="card-label">Mavjud zaxira nuqtalari</p>
          </div>
          <div class="card-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
          </div>
        </div>

        <!-- Baza yozuvlari soni -->
        <div class="summary-card card-green">
          <div class="card-content">
            <h2 class="card-value">{{ (stats.totalDocs || 0).toLocaleString() }} ta</h2>
            <p class="card-label">Bazada jami yozuvlar</p>
          </div>
          <div class="card-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <ellipse cx="12" cy="5" rx="9" ry="3"></ellipse>
              <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"></path>
              <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path>
            </svg>
          </div>
        </div>

        <!-- Avtomatik zaxira holati -->
        <div class="summary-card card-purple">
          <div class="card-content">
            <div class="flex items-center gap-2">
              <h2 class="card-value font-sm-custom text-emerald-600">FAOL (Har 1 soatda)</h2>
            </div>
            <p class="card-label">Avtomatik rejali zaxiralash</p>
          </div>
          <div class="card-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
          </div>
        </div>

        <!-- Oxirgi nusxa -->
        <div class="summary-card card-amber">
          <div class="card-content">
            <h2 class="card-value font-sm-custom">{{ latestBackupRelative }}</h2>
            <p class="card-label">Oxirgi olingan zaxira</p>
          </div>
          <div class="card-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
            </svg>
          </div>
        </div>
      </div>

      <!-- Alohida Jadvallarni Yuklab Olish (Tables Export Bar) -->
      <div class="table-card mt-6">
        <div class="card-header-bar">
          <div>
            <h3 class="table-title">Jadvallarni alohida yuklab olish (.xlsx / JSON)</h3>
            <p class="text-xs text-gray-500 mt-1">Istalgan jadval ma'lumotlarini to'g'ridan-to'g'ri kompyuteringizga haqiqiy Excel (.xlsx) yoki JSON formatida yuklab oling</p>
          </div>
        </div>
        <div class="collections-grid">
          <div class="coll-box" v-for="c in primaryCollections" :key="c.name">
            <div class="coll-info">
              <span class="coll-label">{{ c.label }}</span>
              <span class="coll-count">{{ (c.count || 0).toLocaleString() }} ta yozuv</span>
            </div>
            <div class="coll-actions">
              <button class="btn-export xlsx" @click="exportTable(c.name, 'xlsx')">
                📗 Excel (.xlsx)
              </button>
              <button class="btn-export json" @click="exportTable(c.name, 'json')">
                📄 JSON
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Yuklama Sahifasidagi Tugmalar Boshqaruvi -->
      <div class="table-card mt-6">
        <div class="card-header-bar">
          <div>
            <h3 class="table-title">⚙️ Kafedra yuklamalari sahifasidagi tugmalar boshqaruvi</h3>
            <p class="text-xs text-gray-500 mt-1">Kafedra yuklamalari jadvali yuqorisida "Qayta sync" va "Baza nusxasi" tugmalarini chiqarish yoki o'chirib qo'yish:</p>
          </div>
        </div>
        <div class="p-5 flex items-center gap-6 flex-wrap bg-white">
          <label class="toggle-card">
            <input type="checkbox" v-model="buttonsConfig.showSync" @change="saveButtonsConfig" />
            <div class="toggle-info">
              <span class="toggle-title">🔄 "Qayta sync" tugmasi</span>
              <span class="toggle-desc">HEMIS bilan sinxronizatsiya qilish tugmasini ko'rsatish</span>
            </div>
          </label>
          <label class="toggle-card">
            <input type="checkbox" v-model="buttonsConfig.showBackup" @change="saveButtonsConfig" />
            <div class="toggle-info">
              <span class="toggle-title">🛡️ "Baza nusxasi (Backup)" tugmasi</span>
              <span class="toggle-desc">Yuklama jadvalida Excel yonida tezkor zaxira olish tugmasini ko'rsatish</span>
            </div>
          </label>
        </div>
      </div>

      <!-- Xodimlar Bazasini Yangilash (HEMIS Sync Card) -->
      <div class="table-card mt-6">
        <div class="card-header-bar flex justify-between items-center flex-wrap gap-4">
          <div>
            <h3 class="table-title">👥 Xodimlar bazasini yangilash (HEMIS API)</h3>
            <p class="text-xs text-gray-500 mt-1">HEMIS tizimidan faol ishlayotgan barcha professor-o'qituvchilarni bazaga yuklash va ishdan bo'shaganlarni avtomatik tozalash</p>
          </div>
          <button 
            class="btn-sync-action"
            :disabled="syncingEmployees"
            @click="syncEmployeesDatabase"
          >
            <span v-if="syncingEmployees" class="spinner-small mr-2"></span>
            <span v-else class="mr-2">🔄</span>
            {{ syncingEmployees ? 'Baza yangilanmoqda...' : 'Xodimlar bazasini yangilash' }}
          </button>
        </div>
      </div>

      <!-- Main Table Card: Snapshots & Backups -->
      <div class="table-card mt-6">
        <div class="card-header-bar">
          <div class="flex items-center gap-3">
            <h3 class="table-title">Zaxira nuqtalari va holatga qaytarish (Rollback)</h3>
            <span class="count-badge">{{ backups.length }} ta nuqta</span>
          </div>

          <div class="flex items-center gap-3 flex-wrap">
            <!-- Filter by type -->
            <select v-model="typeFilter" class="filter-type-select">
              <option value="all">Barcha nusxalar</option>
              <option value="MANUAL">Qo'lda olingan (Tezkor nuqtalar)</option>
              <option value="AUTO">Avtomatik olingan</option>
              <option value="ROLLBACK_PRE">Tiklashdan oldingi nuqtalar</option>
            </select>

            <div class="search-input-wrapper">
              <input 
                v-model="searchQuery" 
                type="text" 
                placeholder="Qidirish..." 
                class="search-input"
              />
            </div>
          </div>
        </div>

        <table class="main-table">
          <thead>
            <tr>
              <th class="w-12 text-center">#</th>
              <th>Turi va Izoh</th>
              <th class="w-56">Vaqti (Nisbiy)</th>
              <th class="w-28 text-center">Hajmi</th>
              <th>Jadvallar tarkibi</th>
              <th class="w-56 text-center">Amallar</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="6" class="py-12 text-center text-gray-400">
                <div class="spinner-container">
                  <div class="spinner"></div>
                  <span>Yuklanmoqda...</span>
                </div>
              </td>
            </tr>
            <tr v-else-if="filteredBackups.length === 0">
              <td colspan="6" class="py-12 text-center text-gray-400">
                <div class="empty-state">
                  <p class="empty-text">Zaxira nusxalari topilmadi</p>
                  <button class="btn-create-backup mt-3" @click="openSnapshotModal">
                    ⚡ Tezkor nuqta olish
                  </button>
                </div>
              </td>
            </tr>
            <tr
              v-for="(b, index) in filteredBackups"
              :key="b.filename"
              class="data-row"
            >
              <td class="text-center font-medium text-gray-400">{{ index + 1 }}</td>
              <td>
                <div class="flex flex-col gap-1">
                  <div class="flex items-center gap-2">
                    <span :class="['type-badge', b.type]">
                      {{ b.type === 'AUTO' ? '⏰ Rejali avtomatik' : (b.type === 'ROLLBACK_PRE' ? '🛡️ Xavfsizlik nuqtasi' : '⚡ Tezkor nuqta') }}
                    </span>
                    <span class="filename-text font-mono text-xs text-gray-500">{{ b.filename }}</span>
                  </div>
                  <span class="note-text">{{ b.note || 'Zaxira nusxasi' }}</span>
                </div>
              </td>
              <td>
                <div class="time-col">
                  <span class="time-relative">{{ getRelativeTime(b.createdAt) }}</span>
                  <span class="time-exact">{{ formatDateTime(b.createdAt) }}</span>
                </div>
              </td>
              <td class="text-center">
                <span class="badge-size">{{ b.sizeFormatted }}</span>
              </td>
              <td>
                <div class="counts-wrap" v-if="b.counts">
                  <span class="cnt-tag" title="Kafedra yuklamalari">
                    Yuklamalar: <b>{{ (b.counts.kafedrayuklamas || 0).toLocaleString() }}</b>
                  </span>
                  <span class="cnt-tag" title="Kafedra taqsimoti">
                    Taqsimot: <b>{{ (b.counts.kafedrataqsimots || 0).toLocaleString() }}</b>
                  </span>
                  <span class="cnt-tag" title="O'quv rejalari">
                    Rejalar: <b>{{ (b.counts.curriculums || 0).toLocaleString() }}</b>
                  </span>
                  <span class="cnt-tag" title="Shaxsiy ish rejalar">
                    Shaxsiy: <b>{{ (b.counts.shaxsiyishrejas || 0).toLocaleString() }}</b>
                  </span>
                </div>
                <span v-else class="text-gray-400 text-xs">—</span>
              </td>
              <td class="text-center">
                <div class="action-buttons">
                  <!-- 🔄 Qaytarish (Rollback) -->
                  <button 
                    class="btn-act restore" 
                    title="Bazani aynan shu holatga qaytarish" 
                    @click="openRestoreModal(b)"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="1 4 1 10 7 10"></polyline>
                      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
                    </svg>
                    Qaytarish
                  </button>

                  <!-- ⬇️ Yuklab olish -->
                  <button 
                    class="btn-act download" 
                    title="Faylni yuklab olish" 
                    @click="downloadBackup(b.filename)"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                  </button>

                  <!-- 🗑️ O'chirish -->
                  <button 
                    class="btn-act delete" 
                    title="O'chirish" 
                    @click="openDeleteModal(b)"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Snapshot Creation Modal -->
      <div v-if="snapshotModal.show" class="modal-overlay" @click.self="snapshotModal.show = false">
        <div class="modal-card">
          <div class="modal-header">
            <div class="modal-icon-lightning">⚡</div>
            <h3 class="modal-title">Yangi tezkor nuqta (Snapshot) olish</h3>
          </div>
          <div class="modal-body">
            <p class="text-gray-700 text-sm mb-3">
              Ushbu daqiqadagi barcha ma'lumotlar to'liq saqlanadi. Keyinchalik istalgan vaqtda bazani aynan shu holatga 1 soniyada qaytara olasiz.
            </p>
            <label class="block text-xs font-semibold text-gray-700 mb-1">Nuqta izohi (ixtiyoriy):</label>
            <input 
              v-model="snapshotModal.note" 
              type="text" 
              placeholder="Masalan: Kafedra yuklamalari yangilanishidan oldin" 
              class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500"
              @keyup.enter="executeCreateSnapshot"
            />
          </div>
          <div class="modal-footer">
            <button class="btn-secondary" :disabled="creating" @click="snapshotModal.show = false">
              Bekor qilish
            </button>
            <button class="btn-confirm-snapshot" :disabled="creating" @click="executeCreateSnapshot">
              {{ creating ? 'Nusxa olinmoqda...' : '⚡ Saqlash' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Rollback / Restore Confirmation Modal -->
      <div v-if="restoreModal.show" class="modal-overlay" @click.self="restoreModal.show = false">
        <div class="modal-card">
          <div class="modal-header">
            <div class="modal-icon-warn">🔄</div>
            <h3 class="modal-title">Bazani ushbu holatga qaytarish (Rollback)</h3>
          </div>
          <div class="modal-body">
            <p class="text-gray-700 font-medium">
              Bazani quyidagi nuqtadagi holatga qaytarmoqchimisiz?
            </p>
            <div class="target-file-box mt-2">
              <div class="flex items-center justify-between">
                <span class="font-bold text-blue-700">{{ getRelativeTime(restoreModal.target?.createdAt) }}</span>
                <span class="badge-size">{{ restoreModal.target?.sizeFormatted }}</span>
              </div>
              <span class="text-xs text-gray-600">Sana: {{ formatDateTime(restoreModal.target?.createdAt) }}</span>
              <span class="text-xs text-gray-500 italic mt-1">{{ restoreModal.target?.note || restoreModal.target?.filename }}</span>
            </div>
            <div class="alert-box-warning mt-3">
              <b>🛡️ Xavfsizlik kafolati:</b> Tiklash boshlanishi bilanoq, tizim joriy holatdan avtomatik ravishda <i>Xavfsizlik nuqtasi (Pre-rollback)</i> olib qo'yadi. Hech qanday ma'lumot yo'qolmaydi!
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn-secondary" :disabled="restoring" @click="restoreModal.show = false">
              Bekor qilish
            </button>
            <button class="btn-confirm-restore" :disabled="restoring" @click="executeRestore">
              {{ restoring ? 'Qaytarilmoqda...' : '🔄 Ha, shu holatga qaytarish' }}
            </button>
          </div>
        </div>
      </div>

      <!-- Delete Confirmation Modal -->
      <div v-if="deleteModal.show" class="modal-overlay" @click.self="deleteModal.show = false">
        <div class="modal-card">
          <div class="modal-header">
            <div class="modal-icon-danger">🗑️</div>
            <h3 class="modal-title">Zaxira nuqtasini o'chirish</h3>
          </div>
          <div class="modal-body">
            <p class="text-gray-700">
              Ushbu zaxira nuqtasini serverdan o'chirmoqchimisiz?
            </p>
            <div class="target-file-box mt-2">
              <b>{{ deleteModal.target?.filename }}</b>
              <span class="text-xs text-gray-500">{{ formatDateTime(deleteModal.target?.createdAt) }}</span>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn-secondary" :disabled="deleting" @click="deleteModal.show = false">
              Bekor qilish
            </button>
            <button class="btn-confirm-delete" :disabled="deleting" @click="executeDelete">
              {{ deleting ? 'O\'chirilmoqda...' : 'O\'chirish' }}
            </button>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import axios from "axios";

const API = process.env.VUE_APP_API_URL;

export default {
  name: "Backup",
  data() {
    return {
      loading: false,
      creating: false,
      restoring: false,
      deleting: false,
      stats: {
        totalCollections: 0,
        totalDocs: 0,
        collections: [],
        totalBackups: 0
      },
      backups: [],
      searchQuery: "",
      typeFilter: "all",
      notification: {
        text: "",
        type: "success"
      },
      snapshotModal: {
        show: false,
        note: ""
      },
      restoreModal: {
        show: false,
        target: null
      },
      deleteModal: {
        show: false,
        target: null
      },
      buttonsConfig: {
        showSync: true,
        showBackup: true
      },
      syncingEmployees: false
    };
  },
  computed: {
    primaryCollections() {
      const labelMap = {
        kafedrayuklamas: "Kafedra yuklamalari",
        kafedrataqsimots: "Kafedra taqsimotlari",
        curriculums: "Ishchi o'quv rejalar",
        shaxsiyishrejas: "Shaxsiy ish rejalar",
        kafedras: "Kafedralar ro'yxati",
        auditlogs: "Faoliyat jurnali (Audit)",
        admins: "Foydalanuvchilar",
        employees: "Xodimlar (Faol o'qituvchilar)"
      };
      if (!this.stats.collections) return [];
      return this.stats.collections
        .filter(c => !c.name.startsWith("system.") && c.name !== "departments")
        .map(c => ({
          name: c.name,
          label: labelMap[c.name] || c.name,
          count: c.count
        }));
    },
    latestBackupRelative() {
      if (!this.backups || this.backups.length === 0) return "Mavjud emas";
      return this.getRelativeTime(this.backups[0].createdAt);
    },
    filteredBackups() {
      let list = this.backups;
      if (this.typeFilter !== "all") {
        list = list.filter(b => b.type === this.typeFilter);
      }
      if (!this.searchQuery.trim()) return list;
      const q = this.searchQuery.toLowerCase();
      return list.filter(b => 
        b.filename.toLowerCase().includes(q) || 
        (b.note && b.note.toLowerCase().includes(q))
      );
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    showNotification(text, type = "success") {
      this.notification = { text, type };
      setTimeout(() => {
        if (this.notification.text === text) {
          this.notification.text = "";
        }
      }, 5000);
    },
    async loadData() {
      this.loading = true;
      try {
        const [statsRes, listRes, btnRes] = await Promise.all([
          axios.get(`${API}/backup/stats`),
          axios.get(`${API}/backup/list`),
          axios.get(`${API}/backup/buttons-config`).catch(() => ({ data: null }))
        ]);
        if (statsRes.data?.success) {
          this.stats = statsRes.data.data;
        }
        if (listRes.data?.success) {
          this.backups = listRes.data.data;
        }
        if (btnRes?.data?.success && btnRes?.data?.buttons) {
          this.buttonsConfig = {
            showSync: btnRes.data.buttons.showSync !== false,
            showBackup: btnRes.data.buttons.showBackup !== false
          };
        }
      } catch (err) {
        console.error("Backup data load error:", err);
        this.showNotification("Ma'lumotlarni yuklashda xatolik yuz berdi", "error");
      } finally {
        this.loading = false;
      }
    },
    async syncEmployeesDatabase() {
      this.syncingEmployees = true;
      try {
        const token = localStorage.getItem("token");
        const res = await axios.post(`${API}/employees/sync`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.success) {
          this.showNotification(
            res.data.message || `${res.data.count} ta faol xodim bazada muvaffaqiyatli yangilandi!`,
            "success"
          );
          await this.loadData();
        } else {
          this.showNotification(res.data.error || "Xodimlar bazasini yangilashda xatolik yuz berdi", "error");
        }
      } catch (err) {
        console.error("Employee sync error:", err);
        this.showNotification(
          err.response?.data?.error || err.message || "Xodimlarni yangilashda xatolik yuz berdi",
          "error"
        );
      } finally {
        this.syncingEmployees = false;
      }
    },
    async saveButtonsConfig() {
      try {
        localStorage.setItem("yuklama_btn_sync", this.buttonsConfig.showSync);
        localStorage.setItem("yuklama_btn_backup", this.buttonsConfig.showBackup);
        const res = await axios.post(`${API}/backup/buttons-config`, {
          showSync: this.buttonsConfig.showSync,
          showBackup: this.buttonsConfig.showBackup
        });
        if (res.data?.success) {
          this.showNotification("Yuklama sahifasidagi tugmalar sozlamasi saqlandi!", "success");
        }
      } catch (err) {
        console.error("Save buttons config error:", err);
        this.showNotification("Sozlamani saqlashda xatolik", "error");
      }
    },
    openSnapshotModal() {
      this.snapshotModal = {
        show: true,
        note: ""
      };
    },
    async executeCreateSnapshot() {
      this.creating = true;
      try {
        const res = await axios.post(`${API}/backup/create`, {
          note: this.snapshotModal.note || "Tezkor nuqta (Snapshot)",
          type: "MANUAL"
        });
        if (res.data?.success) {
          this.showNotification(`Tezkor nuqta saqlandi! (${res.data.data.sizeFormatted})`, "success");
          this.snapshotModal.show = false;
          await this.loadData();
        } else {
          this.showNotification(res.data?.error || "Nusxa olishda xatolik", "error");
        }
      } catch (err) {
        console.error("Create snapshot error:", err);
        this.showNotification("Nusxa olishda xatolik: " + (err.response?.data?.error || err.message), "error");
      } finally {
        this.creating = false;
      }
    },
    downloadBackup(filename) {
      const token = localStorage.getItem("token") || "";
      window.open(`${API}/backup/download/${encodeURIComponent(filename)}?token=${encodeURIComponent(token)}`, "_blank");
    },
    exportTable(collectionName, format = "xlsx") {
      const token = localStorage.getItem("token") || "";
      window.open(`${API}/backup/export/${encodeURIComponent(collectionName)}?format=${format}&token=${encodeURIComponent(token)}`, "_blank");
    },
    openRestoreModal(backup) {
      this.restoreModal = {
        show: true,
        target: backup
      };
    },
    async executeRestore() {
      if (!this.restoreModal.target) return;
      this.restoring = true;
      try {
        const filename = this.restoreModal.target.filename;
        const res = await axios.post(`${API}/backup/restore/${encodeURIComponent(filename)}`);
        if (res.data?.success) {
          this.showNotification("Baza tanlangan nuqtadagi holatga to'liq qaytarildi!", "success");
          this.restoreModal.show = false;
          await this.loadData();
        } else {
          this.showNotification(res.data?.error || "Qaytarishda xatolik", "error");
        }
      } catch (err) {
        console.error("Restore error:", err);
        this.showNotification("Qaytarishda xatolik: " + (err.response?.data?.error || err.message), "error");
      } finally {
        this.restoring = false;
      }
    },
    openDeleteModal(backup) {
      this.deleteModal = {
        show: true,
        target: backup
      };
    },
    async executeDelete() {
      if (!this.deleteModal.target) return;
      this.deleting = true;
      try {
        const filename = this.deleteModal.target.filename;
        const res = await axios.delete(`${API}/backup/${encodeURIComponent(filename)}`);
        if (res.data?.success) {
          this.showNotification("Zaxira nuqtasi o'chirildi", "success");
          this.deleteModal.show = false;
          await this.loadData();
        } else {
          this.showNotification(res.data?.error || "O'chirishda xatolik", "error");
        }
      } catch (err) {
        console.error("Delete error:", err);
        this.showNotification("O'chirishda xatolik: " + (err.response?.data?.error || err.message), "error");
      } finally {
        this.deleting = false;
      }
    },
    getRelativeTime(dateStr) {
      if (!dateStr) return "—";
      const now = Date.now();
      const past = new Date(dateStr).getTime();
      const diffSec = Math.floor((now - past) / 1000);

      if (diffSec < 60) return "Hozirgina";
      const diffMin = Math.floor(diffSec / 60);
      if (diffMin < 60) return `${diffMin} daqiqa oldin`;
      const diffHr = Math.floor(diffMin / 60);
      if (diffHr < 24) return `${diffHr} soat oldin`;
      const diffDay = Math.floor(diffHr / 24);
      if (diffDay === 1) return "Kecha";
      if (diffDay < 7) return `${diffDay} kun oldin`;
      return `${Math.floor(diffDay / 7)} hafta oldin`;
    },
    formatDateTime(dateStr) {
      if (!dateStr) return "—";
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return String(dateStr);
      const pad = n => String(n).padStart(2, "0");
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    }
  }
};
</script>

<style scoped>
.page-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #f6f7fb;
}

.page-container {
  flex: 1;
  padding: 24px;
}

.page-header {
  margin-bottom: 20px;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.crumb-link {
  color: #6b7280;
  cursor: pointer;
  transition: color 0.15s;
}

.crumb-link:hover {
  color: #2563eb;
}

.separator {
  color: #9ca3af;
}

.crumb-current {
  color: #111827;
  font-weight: 600;
}

.header-desc {
  font-size: 13px;
  color: #6b7280;
  margin-top: 4px;
}

.btn-refresh {
  display: flex;
  align-items: center;
  gap: 6px;
  background: white;
  border: 1px solid #e5e7eb;
  padding: 8px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-refresh:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

.btn-refresh.spinning svg {
  animation: spin 1s linear infinite;
}

.btn-create-backup {
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  color: white;
  border: none;
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(37, 99, 235, 0.25);
  transition: all 0.2s;
}

.btn-create-backup:hover:not(:disabled) {
  background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
  box-shadow: 0 4px 6px rgba(37, 99, 235, 0.35);
  transform: translateY(-1px);
}

.btn-create-backup:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.btn-sync-employees {
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #0d9488 0%, #0f766e 100%);
  color: white;
  border: none;
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(13, 148, 136, 0.25);
  transition: all 0.2s;
}

.btn-sync-employees:hover:not(:disabled) {
  background: linear-gradient(135deg, #0f766e 0%, #115e59 100%);
  box-shadow: 0 4px 6px rgba(13, 148, 136, 0.35);
  transform: translateY(-1px);
}

.btn-sync-employees:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.btn-sync-action {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #0d9488 0%, #0f766e 100%);
  color: white;
  border: none;
  padding: 9px 20px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(13, 148, 136, 0.25);
  transition: all 0.2s;
}

.btn-sync-action:hover:not(:disabled) {
  background: linear-gradient(135deg, #0f766e 0%, #115e59 100%);
  box-shadow: 0 4px 6px rgba(13, 148, 136, 0.35);
  transform: translateY(-1px);
}

.btn-sync-action:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.notification-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 18px;
  border-radius: 8px;
  margin-top: 12px;
  font-size: 14px;
  font-weight: 500;
}

.notification-banner.success {
  background: #ecfdf5;
  color: #065f46;
  border: 1px solid #a7f3d0;
}

.notification-banner.error {
  background: #fef2f2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.close-notif {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  opacity: 0.6;
}

.close-notif:hover {
  opacity: 1;
}

/* Stat Cards */
.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}

.summary-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  padding: 18px 20px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.card-value {
  font-size: 22px;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.card-value.font-sm-custom {
  font-size: 15px;
  font-weight: 700;
}

.card-label {
  font-size: 13px;
  color: #6b7280;
  margin-top: 4px;
}

.card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 10px;
}

.card-blue .card-icon {
  background: #eff6ff;
  color: #2563eb;
}

.card-green .card-icon {
  background: #ecfdf5;
  color: #10b981;
}

.card-purple .card-icon {
  background: #f5f3ff;
  color: #8b5cf6;
}

.card-amber .card-icon {
  background: #fffbeb;
  color: #f59e0b;
}

/* Collections Grid */
.collections-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 12px;
  padding: 16px 20px;
}

.coll-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 10px 14px;
  border-radius: 8px;
}

.coll-info {
  display: flex;
  flex-direction: column;
}

.coll-label {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
}

.coll-count {
  font-size: 11px;
  color: #64748b;
}

.coll-actions {
  display: flex;
  gap: 6px;
}

.btn-export {
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 6px;
  border: 1px solid;
  cursor: pointer;
  transition: all 0.15s;
}

.toggle-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 18px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  flex: 1;
  min-width: 280px;
}

.toggle-card:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

.toggle-card input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: #3b82f6;
  cursor: pointer;
}

.toggle-info {
  display: flex;
  flex-direction: column;
}

.toggle-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.toggle-desc {
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
}

.btn-export.xlsx,
.btn-export.csv {
  background: #ecfdf5;
  color: #047857;
  border-color: #a7f3d0;
}

.btn-export.xlsx:hover,
.btn-export.csv:hover {
  background: #059669;
  color: #ffffff;
  border-color: #059669;
}

.btn-export.json {
  background: #f1f5f9;
  color: #334155;
  border-color: #cbd5e1;
}

.btn-export.json:hover {
  background: #e2e8f0;
}

/* Table Card */
.table-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  overflow: hidden;
}

.card-header-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  flex-wrap: wrap;
  gap: 12px;
}

.table-title {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.count-badge {
  background: #f3f4f6;
  color: #4b5563;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
}

.filter-type-select {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 7px 10px;
  font-size: 13px;
  background: white;
  outline: none;
}

.search-input {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 7px 12px;
  font-size: 13px;
  width: 220px;
  outline: none;
  transition: border-color 0.15s;
}

.search-input:focus {
  border-color: #2563eb;
}

.main-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.main-table thead tr {
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.main-table th {
  padding: 12px 16px;
  font-weight: 600;
  color: #4b5563;
  text-align: left;
}

.main-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
}

.data-row:hover {
  background-color: #f8fafc;
}

.type-badge {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
}

.type-badge.MANUAL {
  background: #dbeafe;
  color: #1e40af;
}

.type-badge.AUTO {
  background: #fef3c7;
  color: #92400e;
}

.type-badge.ROLLBACK_PRE {
  background: #f3e8ff;
  color: #6b21a8;
}

.note-text {
  font-size: 13px;
  font-weight: 500;
  color: #1f2937;
}

.time-col {
  display: flex;
  flex-direction: column;
}

.time-relative {
  font-weight: 600;
  color: #111827;
  font-size: 13px;
}

.time-exact {
  font-size: 11px;
  color: #6b7280;
}

.badge-size {
  display: inline-block;
  background: #e0f2fe;
  color: #0369a1;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
}

.counts-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.cnt-tag {
  background: #f1f5f9;
  color: #475569;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
}

.cnt-tag b {
  color: #0f172a;
}

.action-buttons {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.btn-act {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-act.restore {
  background: #eff6ff;
  color: #1d4ed8;
  border-color: #bfdbfe;
}

.btn-act.restore:hover {
  background: #dbeafe;
}

.btn-act.download {
  background: #f0fdf4;
  color: #15803d;
  border-color: #bbf7d0;
  padding: 6px 8px;
}

.btn-act.download:hover {
  background: #dcfce7;
}

.btn-act.delete {
  background: #fef2f2;
  color: #b91c1c;
  border-color: #fecaca;
  padding: 6px 8px;
}

.btn-act.delete:hover {
  background: #fee2e2;
}

/* Modals */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(2px);
}

.modal-card {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  animation: modalScale 0.2s ease;
}

@keyframes modalScale {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 24px;
  border-bottom: 1px solid #f3f4f6;
}

.modal-icon-lightning {
  font-size: 24px;
}

.modal-icon-warn {
  font-size: 24px;
}

.modal-icon-danger {
  font-size: 24px;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.modal-body {
  padding: 20px 24px;
  font-size: 14px;
}

.target-file-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 12px 14px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.alert-box-warning {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.5;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 24px;
  background: #f9fafb;
  border-top: 1px solid #f3f4f6;
}

.btn-secondary {
  background: white;
  border: 1px solid #d1d5db;
  color: #374151;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.btn-secondary:hover {
  background: #f3f4f6;
}

.btn-confirm-snapshot {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  color: white;
  border: none;
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.btn-confirm-restore {
  background: #2563eb;
  color: white;
  border: none;
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.btn-confirm-restore:hover {
  background: #1d4ed8;
}

.btn-confirm-delete {
  background: #dc2626;
  color: white;
  border: none;
  padding: 8px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.btn-confirm-delete:hover {
  background: #b91c1c;
}

.spinner {
  width: 18px;
  height: 18px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.spinner-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
</style>
